import type { AxiosError } from "axios";
import api from "@/api";

/** Что включено на бэке (выключатели «Настройки приложения БКоннект» в админке). */
export type AppFeatures = {
  login: boolean;
  promo: boolean;
  /** Только для вошедших: текущая подписка оформлена промокодом и не оплачивалась. */
  promo_subscription?: boolean;
  /** Код из письма: подтвердить почту или войти по 6 цифрам. */
  email_code?: boolean;
  /** Вход в приложение для компьютера через сайт (/app-login). */
  site_login?: boolean;
  /** На Android мини-апп предлагает приложение БКоннект вместо v2RayTun. */
  android_app?: boolean;
};

export const FEATURES_OFF: AppFeatures = { login: false, promo: false };

export async function fetchAppFeatures(): Promise<AppFeatures> {
  try {
    const resp = await api.get<AppFeatures>("/app/features/");
    return { ...FEATURES_OFF, ...resp.data };
  } catch {
    // бэк без /app/features/ или сеть недоступна — считаем, что всё выключено
    return FEATURES_OFF;
  }
}

export type AppLoginInfo = {
  device_name: string;
  platform: string;
  created_at: string;
};

const initDataHeaders = (initData: string) => ({
  "X-Telegram-Init-Data": initData,
});

/** Что за устройство просит вход. Личность — по подписи Telegram, не по JWT. */
export async function checkAppLogin(code: string, initData: string) {
  const resp = await api.post<AppLoginInfo>(
    "/app/login/check/",
    { code },
    { headers: initDataHeaders(initData) }
  );
  return resp.data;
}

export async function confirmAppLogin(code: string, initData: string) {
  const resp = await api.post<{ ok: true }>(
    "/app/login/confirm/",
    { code },
    { headers: initDataHeaders(initData) }
  );
  return resp.data;
}

export type AppLink = {
  /** bkvpn://login/<одноразовый билет> — открывает BK VPN и входит в аккаунт */
  link: string;
  expires_at: string;
};

/** «Добавить в BK VPN»: одноразовая ссылка входа в приложение на этом компьютере (10 минут). */
export async function createAppLink() {
  const resp = await api.post<AppLink>("/app/link/start/", {});
  return resp.data;
}

export type PromoResult = {
  ok: true;
  expires_at: string;
  config_url: string | null;
  warning: string | null;
};

export async function activatePromo(code: string) {
  const resp = await api.post<PromoResult>("/app/promo/", { code });
  return resp.data;
}

/**
 * Текст ошибки для человека. Бэк кладёт его в `error`; 404 без текста значит,
 * что функция выключена в админке.
 */
export function appErrorMessage(
  error: unknown,
  fallback: string,
  disabled: string
): string {
  const e = error as AxiosError<{ error?: string }>;
  const text = e.response?.data?.error;
  if (text) return text;
  if (e.response?.status === 404) return disabled;
  return fallback;
}
